import { useMutation } from "@apollo/client";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { GlobalConfigContext } from "../context/GlobalConfigContext";
import { getStoreGroupsSummary } from "../graphql/queries/getStoreGroupsSummary";

export const useStoreGroupsSummary = () => {
  const { tagsData } = useContext(GlobalConfigContext);
  const [storeGroupsData, setStoreGroupsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilterState] = useState();
  const statusRef = useRef();
  const [executeFetch] = useMutation(getStoreGroupsSummary);

  const fetchStoreGroups = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = statusRef.current;
      const result = await executeFetch({
        variables: { limit: 1000, offset: 0, ...(status ? { status } : {}) },
      });
      setStoreGroupsData(result?.data?.getStoreGroupsSummary ?? null);
    } catch (error) {
      console.error("Failed to fetch store groups summary:", error);
    } finally {
      setIsLoading(false);
    }
  }, [executeFetch]);

  // "All" is represented by undefined so no status is sent.
  const setStatusFilter = useCallback(
    (status) => {
      statusRef.current = status || undefined;
      setStatusFilterState(status || undefined);
      return fetchStoreGroups();
    },
    [fetchStoreGroups]
  );

  const storeGroups = useMemo(() => {
    const queryGroups = storeGroupsData?.items || [];
    // Tag-only groups carry no status, so they can't be matched by a status filter.
    const tagGroupNames = statusFilter ? [] : tagsData?.storeGroups || [];
    const queryGroupNames = queryGroups.map((item) => item.name);
    const mergedGroupNames = [...new Set([...tagGroupNames, ...queryGroupNames])];

    const queryByName = new Map(queryGroups.map((item) => [item.name, item]));

    return mergedGroupNames.map((name) => {
      const queryItem = queryByName.get(name);
      return {
        name,
        storeCount: queryItem?.amountOfStores ?? 0,
        zipCodes: queryItem?.zipCodes || [],
        status: queryItem?.status,
      };
    });
  }, [storeGroupsData, tagsData, statusFilter]);

  return {
    storeGroups,
    loading: isLoading,
    refetch: fetchStoreGroups,
    statusFilter,
    setStatusFilter,
  };
};
